import type { CSSProperties, ReactNode } from 'react'
import type { ModuleId } from '@/constants/modules'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/utils/cn'

/* ── Primitives ─────────────────────────────────────────────── */

function skeletonDelay(index: number): CSSProperties {
  return { '--skeleton-i': index } as CSSProperties
}

function SkeletonFrame({
  children,
  className,
  label = 'Loading content',
}: {
  children: ReactNode
  className?: string
  label?: string
}) {
  return (
    <div
      className={cn('animate-fade-in', className)}
      role="status"
      aria-busy="true"
      aria-label={label}
    >
      {children}
    </div>
  )
}

function SkeletonShell({
  children,
  className,
}: {
  children: ReactNode
  className?: string
  moduleId?: ModuleId
  accentClass?: string
}) {
  return (
    <div
      className={cn(
        'surface-opaque overflow-hidden rounded-xl',
        className,
      )}
    >
      <div className="h-1 w-full shrink-0 bg-primary/70" aria-hidden />
      {children}
    </div>
  )
}

function Stagger({
  count,
  className,
  children,
}: {
  count: number
  className?: string
  children: (index: number) => ReactNode
}) {
  return (
    <div className={cn('skeleton-stagger', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={skeletonDelay(i)}>
          {children(i)}
        </div>
      ))}
    </div>
  )
}

function KpiTileSkeleton({ index = 0 }: { index?: number }) {
  return (
    <div
      className="glass-panel relative overflow-hidden rounded-xl p-2.5 sm:p-3"
      style={skeletonDelay(index)}
    >
      <Skeleton className="mb-2 h-3 w-16 rounded-md" />
      <Skeleton className="h-7 w-20 rounded-lg" />
      <Skeleton className="mt-1.5 h-2.5 w-14 rounded-md" />
    </div>
  )
}

function KpiGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <KpiTileSkeleton key={i} index={i} />
      ))}
    </div>
  )
}

function FilterToolbarSkeleton() {
  return (
    <div className="toolbar-panel glass-panel flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <Skeleton className="h-9 w-full max-w-sm rounded-xl" />
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-9 w-28 rounded-xl" />
        <Skeleton className="h-9 w-32 rounded-xl" />
        <Skeleton className="hidden h-9 w-24 rounded-xl sm:block" />
      </div>
    </div>
  )
}

function TableHeaderSkeleton({ columns = 5 }: { columns?: number }) {
  return (
    <div className="flex items-center gap-3 border-b border-border/50 bg-muted/25 px-3 py-2.5 sm:px-4">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn('h-3 rounded-md', i === 0 ? 'w-24' : i === columns - 1 ? 'ml-auto w-8' : 'w-16')}
          style={skeletonDelay(i)}
        />
      ))}
    </div>
  )
}

function TableRowSkeleton({ index }: { index: number; columns?: number }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 border-b border-border/35 px-3 py-3 last:border-b-0 sm:px-4',
        index % 2 === 1 && 'bg-muted/10',
      )}
    >
      <Skeleton className="h-4 w-[28%] max-w-[9rem] rounded-md" style={skeletonDelay(index)} />
      <Skeleton className="hidden h-4 w-[18%] rounded-md sm:block" />
      <Skeleton className="h-4 w-[12%] rounded-md" />
      <Skeleton className="hidden h-4 w-[14%] rounded-md md:block" />
      <Skeleton className="ml-auto h-7 w-7 shrink-0 rounded-lg" />
    </div>
  )
}

function TableBodySkeleton({ rows, columns = 5 }: { rows: number; columns?: number }) {
  return (
    <Stagger count={rows}>
      {(i) => <TableRowSkeleton index={i} columns={columns} />}
    </Stagger>
  )
}

function PaginationSkeleton() {
  return (
    <div className="flex items-center justify-between gap-3 px-1 pt-1">
      <Skeleton className="h-3 w-28 rounded-md" />
      <div className="flex gap-1.5">
        <Skeleton className="h-8 w-8 rounded-lg" />
        <Skeleton className="h-8 w-8 rounded-lg" />
        <Skeleton className="h-8 w-8 rounded-lg" />
      </div>
    </div>
  )
}

function ChartBarsSkeleton({ bars = 8, heightClass = 'h-40 sm:h-48' }: { bars?: number; heightClass?: string }) {
  return (
    <div className={cn('flex items-end gap-1.5 sm:gap-2', heightClass)}>
      {Array.from({ length: bars }).map((_, i) => (
        <Skeleton
          key={i}
          className="flex-1 rounded-t-lg"
          style={{ height: `${32 + (i % 4) * 16}%`, ...skeletonDelay(i) }}
        />
      ))}
    </div>
  )
}

function HorizontalBarsSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3" style={skeletonDelay(i)}>
          <Skeleton className="h-3 w-14 shrink-0 rounded-md" />
          <Skeleton className="h-6 flex-1 rounded-md" style={{ maxWidth: `${55 + (i % 3) * 12}%` }} />
        </div>
      ))}
    </div>
  )
}

/* ── Public layouts ─────────────────────────────────────────── */

/** List/table pages — filters, themed table, pagination */
export function TableSkeleton({
  rows = 6,
  moduleId,
  showMobileCards = true,
  mobileBreakpoint = 'sm',
}: {
  rows?: number
  moduleId?: ModuleId
  showMobileCards?: boolean
  /** Breakpoint where the mobile-card skeleton gives way to the table
   * skeleton — must match the real content's own card/table switch, or the
   * loading state shows the wrong shape for that width band. Default 'sm'
   * (640) matches every other current caller's real content. Pass 'md' (768)
   * for a list page whose real content switches at the app's own mobile
   * boundary (`useIsMobile()` / `BREAKPOINTS.md`) instead — see
   * SalesLeadsListPage, the only 'md' caller today. */
  mobileBreakpoint?: 'sm' | 'md'
}) {
  return (
    <SkeletonFrame className="space-y-3" label="Loading list">
      <FilterToolbarSkeleton />
      <SkeletonShell moduleId={moduleId}>
        <TableHeaderSkeleton />
        <TableBodySkeleton rows={rows} />
      </SkeletonShell>
      {showMobileCards ? (
        <div className={cn('space-y-2', mobileBreakpoint === 'md' ? 'md:hidden' : 'sm:hidden')}>
          <Stagger count={Math.min(rows, 4)}>
            {(i) => (
              <div
                className="overflow-hidden rounded-xl border border-border/60 bg-card p-3 shadow-card ring-1 ring-black/[0.04] dark:ring-white/[0.06]"
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <Skeleton className="h-4 w-24 rounded-md" style={skeletonDelay(i)} />
                  <Skeleton className="h-5 w-14 rounded-full" />
                </div>
                <Skeleton className="mb-1.5 h-3 w-full rounded-md" />
                <Skeleton className="h-3 w-[66%] rounded-md" />
              </div>
            )}
          </Stagger>
        </div>
      ) : null}
      <PaginationSkeleton />
    </SkeletonFrame>
  )
}

/** Route lazy-load, auth hydrate, generic full page */
export function PageSkeleton() {
  return (
    <SkeletonFrame className="space-y-5" label="Loading page">
      <div className="space-y-2.5 border-b border-border/60 pb-5">
        <Skeleton className="h-8 w-52 max-w-[70%] rounded-xl" />
        <Skeleton className="h-4 w-80 max-w-full rounded-lg" />
      </div>
      <KpiGridSkeleton />
      <div className="grid gap-3 lg:grid-cols-2">
        <SkeletonShell>
          <div className="space-y-4 p-4 sm:p-5">
            <Skeleton className="h-4 w-28 rounded-md" />
            <Skeleton className="mx-auto h-32 w-32 rounded-full" />
            <div className="flex justify-center gap-3">
              <Skeleton className="h-3 w-16 rounded-md" />
              <Skeleton className="h-3 w-16 rounded-md" />
            </div>
          </div>
        </SkeletonShell>
        <SkeletonShell>
          <div className="space-y-3 p-4 sm:p-5">
            <Skeleton className="h-4 w-32 rounded-md" />
            <ChartBarsSkeleton bars={6} heightClass="h-36" />
          </div>
        </SkeletonShell>
      </div>
      <SkeletonShell>
        <div className="space-y-2 border-b border-border/50 px-4 py-3">
          <Skeleton className="h-5 w-40 rounded-lg" />
          <Skeleton className="h-3 w-56 rounded-md" />
        </div>
        <TableHeaderSkeleton />
        <TableBodySkeleton rows={5} />
      </SkeletonShell>
    </SkeletonFrame>
  )
}

/** Dashboard desktop + mobile — mirrors live L0/L1/L2 surface hierarchy */
export function DashboardSkeleton() {
  return (
    <SkeletonFrame className="space-y-3" label="Loading dashboard">
      <div className="glass-surface flex items-center justify-between gap-3 rounded-2xl border border-white/65 px-4 py-3.5 dark:border-white/10">
        <div className="space-y-2">
          <Skeleton className="h-5 w-40 rounded-lg" />
          <Skeleton className="h-3 w-28 rounded-md" />
        </div>
        <Skeleton className="h-9 w-9 shrink-0 rounded-lg sm:w-24" />
      </div>
      <KpiGridSkeleton />
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="surface-panel overflow-hidden rounded-xl">
          <div className="space-y-4 p-4 sm:p-5">
            <Skeleton className="h-4 w-28 rounded-md" />
            <Skeleton className="mx-auto h-32 w-32 rounded-full" />
            <div className="flex flex-wrap justify-center gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-3 w-14 rounded-md" style={skeletonDelay(i)} />
              ))}
            </div>
          </div>
        </div>
        <div className="surface-panel overflow-hidden rounded-xl">
          <div className="space-y-3 p-4 sm:p-5">
            <Skeleton className="h-4 w-32 rounded-md" />
            <ChartBarsSkeleton bars={6} heightClass="h-36" />
          </div>
        </div>
      </div>
      <div className="surface-panel overflow-hidden rounded-xl">
        <div className="border-b border-border/50 px-4 py-3">
          <Skeleton className="h-4 w-28 rounded-md" />
        </div>
        <Stagger count={4}>
          {(i) => (
            <div className="flex items-center justify-between gap-3 border-b border-border/35 px-4 py-3 last:border-b-0">
              <Skeleton className="h-4 flex-1 rounded-md" style={skeletonDelay(i)} />
              <Skeleton className="h-3 w-16 rounded-md" />
            </div>
          )}
        </Stagger>
      </div>
    </SkeletonFrame>
  )
}

/** Reports overview */
export function ChartSkeleton() {
  return (
    <SkeletonFrame className="space-y-3" label="Loading charts">
      <KpiGridSkeleton />
      <SkeletonShell moduleId="reports">
        <div className="space-y-4 p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-5 w-36 rounded-lg" />
            <Skeleton className="h-8 w-24 rounded-lg" />
          </div>
          <ChartBarsSkeleton />
        </div>
      </SkeletonShell>
      <div className="grid gap-3 md:grid-cols-2">
        <SkeletonShell moduleId="reports">
          <div className="space-y-3 p-4">
            <Skeleton className="h-4 w-28 rounded-md" />
            <HorizontalBarsSkeleton rows={5} />
          </div>
        </SkeletonShell>
        <SkeletonShell moduleId="reports">
          <div className="space-y-3 p-4">
            <Skeleton className="h-4 w-28 rounded-md" />
            <HorizontalBarsSkeleton rows={5} />
          </div>
        </SkeletonShell>
      </div>
      <SkeletonShell moduleId="reports">
        <div className="border-b border-border/50 px-4 py-3">
          <Skeleton className="h-5 w-32 rounded-lg" />
        </div>
        <TableBodySkeleton rows={4} />
      </SkeletonShell>
    </SkeletonFrame>
  )
}

/** Reports breakdown tab */
export function BreakdownSkeleton() {
  return (
    <SkeletonFrame className="space-y-3" label="Loading breakdown">
      <SkeletonShell moduleId="reports">
        <div className="space-y-3 p-4 sm:p-5">
          <Skeleton className="h-5 w-40 rounded-lg" />
          <ChartBarsSkeleton bars={10} heightClass="h-44 sm:h-52" />
        </div>
      </SkeletonShell>
      <SkeletonShell moduleId="reports">
        <div className="border-b border-border/50 px-4 py-3">
          <Skeleton className="h-5 w-36 rounded-lg" />
        </div>
        <TableHeaderSkeleton columns={6} />
        <TableBodySkeleton rows={8} columns={6} />
      </SkeletonShell>
    </SkeletonFrame>
  )
}

/** Reports trend chart area */
export function TrendChartSkeleton() {
  return (
    <SkeletonFrame label="Loading trend">
      <SkeletonShell moduleId="reports">
        <div className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-32 rounded-lg" />
            <Skeleton className="h-3 w-24 rounded-md" />
          </div>
          <div className="grid grid-cols-3 gap-1 rounded-xl bg-muted/50 p-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-8 rounded-lg" style={skeletonDelay(i)} />
            ))}
          </div>
          <ChartBarsSkeleton bars={12} heightClass="h-36 sm:h-44" />
        </div>
      </SkeletonShell>
    </SkeletonFrame>
  )
}

/** Detail exports page */
export function ExportsSkeleton() {
  return (
    <SkeletonFrame className="space-y-3" label="Loading exports">
      <div className="grid grid-cols-2 gap-1 rounded-xl bg-muted/50 p-1 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-9 rounded-lg" style={skeletonDelay(i)} />
        ))}
      </div>
      <FilterToolbarSkeleton />
      <SkeletonShell moduleId="reports">
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-2" style={skeletonDelay(i)}>
              <Skeleton className="h-3 w-20 rounded-md" />
              <Skeleton className="h-9 w-full rounded-xl" />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 border-t border-border/50 px-4 py-3">
          <Skeleton className="h-9 w-28 rounded-xl" />
          <Skeleton className="h-9 w-28 rounded-xl" />
        </div>
      </SkeletonShell>
    </SkeletonFrame>
  )
}

/** Dispatch / purchase detail */
export function DocumentViewSkeleton({ rows = 4, moduleId }: { rows?: number; moduleId?: ModuleId }) {
  return (
    <SkeletonFrame className="space-y-3" label="Loading document">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonShell key={i} moduleId={moduleId}>
            <div className="space-y-2 p-3">
              <Skeleton className="h-3 w-16 rounded-md" style={skeletonDelay(i)} />
              <Skeleton className="h-5 w-28 rounded-md" />
            </div>
          </SkeletonShell>
        ))}
      </div>
      <SkeletonShell moduleId={moduleId}>
        <div className="flex flex-wrap items-center justify-between gap-3 p-3">
          <Skeleton className="h-4 w-24 rounded-md" />
          <Skeleton className="h-8 w-32 rounded-lg" />
        </div>
      </SkeletonShell>
      <SkeletonShell moduleId={moduleId}>
        <TableHeaderSkeleton columns={6} />
        <TableBodySkeleton rows={rows} columns={6} />
      </SkeletonShell>
    </SkeletonFrame>
  )
}

/** Create / edit forms (documents, partners, users) */
export function FormSkeleton({ sections = 2, fieldsPerSection = 4 }: { sections?: number; fieldsPerSection?: number }) {
  return (
    <SkeletonFrame className="mx-auto w-full max-w-3xl space-y-4" label="Loading form">
      {Array.from({ length: sections }).map((_, s) => (
        <SkeletonShell key={s}>
          <div className="border-b border-border/50 px-4 py-2.5">
            <Skeleton className="h-4 w-28 rounded-md" style={skeletonDelay(s)} />
          </div>
          <div className="grid gap-3 p-4 sm:grid-cols-2">
            {Array.from({ length: fieldsPerSection }).map((_, f) => (
              <div key={f} className="space-y-1.5" style={skeletonDelay(s * 10 + f)}>
                <Skeleton className="h-3 w-20 rounded-md" />
                <Skeleton className="h-9 w-full rounded-xl" />
              </div>
            ))}
          </div>
        </SkeletonShell>
      ))}
      <SkeletonShell>
        <div className="border-b border-border/50 px-4 py-2.5">
          <Skeleton className="h-4 w-24 rounded-md" />
        </div>
        <TableHeaderSkeleton columns={4} />
        <TableBodySkeleton rows={3} columns={4} />
      </SkeletonShell>
      <div className="flex justify-end gap-2">
        <Skeleton className="h-9 w-24 rounded-xl" />
        <Skeleton className="h-9 w-28 rounded-xl" />
      </div>
    </SkeletonFrame>
  )
}

/** User / partner detail view */
export function DetailSkeleton({ moduleId = 'user-management' }: { moduleId?: ModuleId }) {
  return (
    <SkeletonFrame className="space-y-4" label="Loading details">
      <div className="flex items-center gap-4">
        <Skeleton className="h-14 w-14 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-5 w-44 max-w-full rounded-lg" />
          <div className="flex gap-2">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-1.5 rounded-xl border border-border/50 p-3" style={skeletonDelay(i)}>
            <Skeleton className="h-3 w-16 rounded-md" />
            <Skeleton className="h-4 w-full rounded-md" />
          </div>
        ))}
      </div>
      <SkeletonShell moduleId={moduleId}>
        <div className="border-b border-border/50 px-4 py-3">
          <Skeleton className="h-4 w-32 rounded-md" />
        </div>
        <div className="space-y-2 p-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3" style={skeletonDelay(i)}>
              <Skeleton className="h-4 w-32 rounded-md" />
              <Skeleton className="h-6 flex-1 rounded-md" />
            </div>
          ))}
        </div>
      </SkeletonShell>
    </SkeletonFrame>
  )
}

/** Permissions matrix page */
export function PermissionMatrixSkeleton() {
  return (
    <SkeletonFrame className="space-y-4" label="Loading permissions">
      <div className="grid grid-cols-3 gap-1 rounded-xl bg-muted/50 p-1">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-9 rounded-lg" style={skeletonDelay(i)} />
        ))}
      </div>
      <SkeletonShell moduleId="user-management">
        <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
          <Skeleton className="h-5 w-36 rounded-lg" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-24 rounded-lg" />
            <Skeleton className="h-8 w-24 rounded-lg" />
          </div>
        </div>
        <div className="divide-y divide-border/40">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3" style={skeletonDelay(i)}>
              <Skeleton className="h-4 w-40 rounded-md" />
              <div className="ml-auto flex gap-3">
                {Array.from({ length: 4 }).map((_, j) => (
                  <Skeleton key={j} className="h-5 w-5 rounded-md" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </SkeletonShell>
    </SkeletonFrame>
  )
}

/** Settings / schedule panels */
export function SettingsPanelSkeleton() {
  return (
    <SkeletonFrame label="Loading settings">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-1.5" style={skeletonDelay(i)}>
            <Skeleton className="h-3 w-24 rounded-md" />
            <Skeleton className="h-9 w-full rounded-xl" />
          </div>
        ))}
      </div>
    </SkeletonFrame>
  )
}

/** Selector dropdowns, compact lists */
export function InlineListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-1 px-1 py-1">
      <Stagger count={rows}>
        {(i) => (
          <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
            <Skeleton className="h-4 w-4 shrink-0 rounded-sm" style={skeletonDelay(i)} />
            <div className="min-w-0 flex-1 space-y-1">
              <Skeleton className="h-3.5 w-full rounded-md" />
              <Skeleton className="hidden h-2.5 w-[66%] rounded-md sm:block" />
            </div>
          </div>
        )}
      </Stagger>
    </div>
  )
}

/** Mobile card list */
export function MobileListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2 sm:hidden">
      <Stagger count={rows}>
        {(i) => (
          <div
            className="overflow-hidden rounded-xl border border-border/60 bg-card p-3.5 shadow-card ring-1 ring-black/[0.04] dark:ring-white/[0.06]"
          >
            <div className="mb-2.5 flex items-start justify-between gap-2">
              <Skeleton className="h-4 w-28 rounded-md" style={skeletonDelay(i)} />
              <Skeleton className="h-5 w-14 rounded-full" />
            </div>
            <Skeleton className="mb-2 h-3 w-full rounded-md" />
            <Skeleton className="h-3 w-[80%] rounded-md" />
          </div>
        )}
      </Stagger>
    </div>
  )
}

/** Global overlay card */
export function LoadingPulseSkeleton() {
  return (
    <div className="glass-panel flex w-[min(20rem,90vw)] flex-col gap-4 rounded-xl px-6 py-5 shadow-elevated">
      <div className="flex items-center gap-3">
        <Skeleton className="h-11 w-11 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-3.5 w-32 rounded-md" />
          <Skeleton className="h-2.5 w-24 rounded-md" />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-2 w-full rounded-full" />
        <Skeleton className="h-2 w-[85%] rounded-full" />
        <Skeleton className="h-2 w-[65%] rounded-full" />
      </div>
    </div>
  )
}
